// Multi-step campaign creation/editing wizard.
// Orchestrates steps: Setup → Content → Layout (articles only) → Review → Send.

import { ArrowLeft, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useAudiences } from '@/api/hooks/use-audiences'
import type { CreateCampaignRequest, UpdateCampaignRequest } from '@/api/hooks/use-campaigns'
import { useCampaign, useCreateCampaign, useUpdateCampaign } from '@/api/hooks/use-campaigns'
import { useWpGroupDetail } from '@/api/hooks/use-wordpress'
import type { WpArticle } from '@/api/hooks/use-wordpress'
import { renderFullNewsletter } from '@/lib/newsletter-template'
import { CampaignStepContent } from '@/pages/campaigns/campaign-step-content'
import { CampaignStepLayout } from '@/pages/campaigns/campaign-step-layout'
import { CampaignStepReview } from '@/pages/campaigns/campaign-step-review'
import { CampaignStepSend } from '@/pages/campaigns/campaign-step-send'
import { CampaignStepSetup } from '@/pages/campaigns/campaign-step-setup'

type ContentType = 'articles' | 'html'
type Step = 'setup' | 'content' | 'layout' | 'review' | 'send'

const STEP_ORDER: Step[] = ['setup', 'content', 'layout', 'review', 'send']
const STEP_LABELS: Record<Step, string> = {
  setup: 'Setup',
  content: 'Content',
  layout: 'Layout',
  review: 'Review',
  send: 'Send',
}

export function CampaignWizardPage(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const isCreateMode = !id

  const { data: existingCampaign, isLoading } = useCampaign(id ?? '')
  const createCampaign = useCreateCampaign()
  const updateCampaign = useUpdateCampaign()

  // Step state
  const [step, setStep] = useState<Step>('setup')

  // Form state
  const [name, setName] = useState('')
  const [audienceId, setAudienceId] = useState('')
  const [subject, setSubject] = useState('')
  const [fromAddress, setFromAddress] = useState('')
  const [replyTo, setReplyTo] = useState('')
  const [contentType, setContentType] = useState<ContentType>('html')
  const [htmlContent, setHtmlContent] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [articles, setArticles] = useState<WpArticle[]>([])

  // Load existing campaign into form
  useEffect(() => {
    if (existingCampaign) {
      setName(existingCampaign.name)
      setAudienceId(existingCampaign.audience_id)
      setSubject(existingCampaign.subject)
      setFromAddress(existingCampaign.from_address)
      setReplyTo(existingCampaign.reply_to ?? '')
      setHtmlContent(existingCampaign.html_content ?? '')
      setTemplateId(existingCampaign.template_id ?? '')
    }
  }, [existingCampaign])

  // Sync articles when group changes
  const { data: groupDetail } = useWpGroupDetail(selectedGroupId)
  useEffect(() => {
    if (groupDetail?.articles) setArticles(groupDetail.articles)
  }, [groupDetail])

  // Audience label for display
  const { data: audienceData } = useAudiences()
  const audienceLabel =
    (audienceData?.data ?? []).find((a) => a.id === audienceId)?.name ?? 'Unknown'

  // Steps that apply for current content type
  const activeSteps = STEP_ORDER.filter((s) => {
    if (s === 'layout') return contentType === 'articles'
    return true
  })

  function nextStep(): void {
    const idx = activeSteps.indexOf(step)
    const next = activeSteps[idx + 1]
    if (next) setStep(next)
  }

  function prevStep(): void {
    const idx = activeSteps.indexOf(step)
    const prev = activeSteps[idx - 1]
    if (prev) setStep(prev)
  }

  // Build the final HTML content for articles mode
  function buildHtmlForArticles(): string {
    if (contentType !== 'articles' || articles.length === 0) return htmlContent
    try {
      return renderFullNewsletter(
        articles.map((a) => ({
          title: a.title,
          excerpt: a.excerpt,
          cover_image_url: a.cover_image_url,
          author: a.author,
          permalink: a.permalink,
          categories: a.categories,
        })),
        name || 'Newsletter',
        new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      )
    } catch {
      return htmlContent
    }
  }

  function handleSaveDraft(): void {
    const finalHtml = contentType === 'articles' ? buildHtmlForArticles() : htmlContent

    if (isCreateMode) {
      const payload: CreateCampaignRequest = {
        audience_id: audienceId,
        name,
        subject,
        from_address: fromAddress,
        reply_to: replyTo || undefined,
        html_content: finalHtml || undefined,
        template_id: templateId || undefined,
      }
      createCampaign.mutate(payload, {
        onSuccess: (result) => navigate(`/campaigns/${result.id}/edit`, { replace: true }),
      })
    } else {
      if (!id) return
      const data: UpdateCampaignRequest = {
        audience_id: audienceId,
        name,
        subject,
        from_address: fromAddress,
        reply_to: replyTo || undefined,
        html_content: finalHtml || undefined,
        template_id: templateId || undefined,
      }
      updateCampaign.mutate({ id, data })
    }
  }

  const isSaving = createCampaign.isPending || updateCampaign.isPending

  if (!isCreateMode && isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading campaign...</div>
  }

  const reviewHtml = contentType === 'articles' ? buildHtmlForArticles() : htmlContent

  return (
    <div className="space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-surface-3 pb-4">
        <div className="flex items-center gap-3">
          <Link to="/campaigns" className="rounded-lg p-1.5 hover:bg-surface-2">
            <ArrowLeft className="h-5 w-5 text-text-secondary" />
          </Link>
          <h1 className="text-xl font-semibold text-text-primary">
            {isCreateMode ? 'New Campaign' : `Edit: ${name}`}
          </h1>
        </div>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={isSaving}
          className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Saving...' : 'Save Draft'}
        </button>
      </div>

      {/* Step indicator */}
      <StepIndicator steps={activeSteps} currentStep={step} onStepClick={setStep} />

      {/* Step content */}
      {step === 'setup' && (
        <CampaignStepSetup
          name={name}
          onNameChange={setName}
          audienceId={audienceId}
          onAudienceChange={setAudienceId}
          fromAddress={fromAddress}
          onFromAddressChange={setFromAddress}
          replyTo={replyTo}
          onReplyToChange={setReplyTo}
          subject={subject}
          onSubjectChange={setSubject}
          contentType={contentType}
          onContentTypeChange={setContentType}
          onNext={nextStep}
        />
      )}

      {step === 'content' && (
        <CampaignStepContent
          contentType={contentType}
          selectedGroupId={selectedGroupId}
          onGroupIdChange={setSelectedGroupId}
          htmlContent={htmlContent}
          onHtmlContentChange={setHtmlContent}
          onBack={prevStep}
          onNext={nextStep}
        />
      )}

      {step === 'layout' && contentType === 'articles' && (
        <CampaignStepLayout
          articles={articles}
          onArticlesReorder={setArticles}
          templateId={templateId}
          onTemplateIdChange={setTemplateId}
          campaignName={name}
          onBack={prevStep}
          onNext={nextStep}
        />
      )}

      {step === 'review' && (
        <CampaignStepReview
          name={name}
          audienceLabel={audienceLabel}
          fromAddress={fromAddress}
          subject={subject}
          replyTo={replyTo}
          contentMode={contentType === 'articles' ? 'group' : 'html'}
          selectedGroupId={selectedGroupId}
          templateId={templateId}
          htmlContent={reviewHtml}
          onBack={prevStep}
          onNext={nextStep}
        />
      )}

      {step === 'send' && id && (
        <CampaignStepSend campaignId={id} audienceLabel={audienceLabel} onBack={prevStep} />
      )}

      {step === 'send' && !id && (
        <div className="mx-auto max-w-2xl space-y-4">
          <p className="text-sm text-text-secondary">
            Save this campaign as a draft first, then you can send or schedule it.
          </p>
          <div className="flex justify-between">
            <button
              type="button"
              onClick={prevStep}
              className="rounded-lg border border-surface-3 px-5 py-2.5 text-sm font-medium text-text-primary hover:bg-surface-1"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSaving}
              className="rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Draft'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function StepIndicator({
  steps,
  currentStep,
  onStepClick,
}: {
  steps: Step[]
  currentStep: Step
  onStepClick: (s: Step) => void
}): React.JSX.Element {
  const currentIdx = steps.indexOf(currentStep)

  return (
    <div className="flex items-center justify-center gap-2">
      {steps.map((s, i) => {
        const isDone = i < currentIdx
        const isCurrent = s === currentStep
        return (
          <div key={s} className="flex items-center gap-2">
            {i > 0 && (
              <div
                className={`h-px w-8 ${isDone ? 'bg-brand-500' : 'bg-surface-3'}`}
              />
            )}
            <button
              type="button"
              onClick={() => onStepClick(s)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                isCurrent
                  ? 'bg-brand-500 text-white'
                  : isDone
                    ? 'bg-brand-100 text-brand-700 hover:bg-brand-200'
                    : 'bg-surface-2 text-text-tertiary hover:bg-surface-3'
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold">
                {i + 1}
              </span>
              {STEP_LABELS[s]}
            </button>
          </div>
        )
      })}
    </div>
  )
}
